import cn from 'classnames';
import * as React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { preload } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { fetcher } from '@/api';
import { Page } from '@/features/page/types';
import usePublicTranslation from '@/hooks/usePublicTranslation';

import ImageCategoryTitleTemplate, { NAME as IMAGE_CATEGORY_TITLE_TEMPLATE } from './templates/ImageCategoryTitle';
import ImageLeftTemplate, { NAME as IMAGE_LEFT_TEMPLATE } from './templates/ImageLeft';
import TitleDateTemplate, { NAME as TITLE_DATE_TEMPLATE } from './templates/TitleDate';

const CATEGORY_ATTRIBUTE = 'data-category';
const CATEGORY_QUERY_PARAM = 'category';

const DEFAULT_LIMIT = 10;

type Category = {
  label: string;
  value: string;
};

type Pages = {
  rows: Pick<Page, 'title' | 'alias' | 'rawBody' | 'binders' | 'meta' | 'createdOn'>[];
  count: number;
};

type Props = {
  type?: string;
  template?: string;
  customFiltersByGroups?: Record<string, string[]>;
  mainGroupAlias?: string;
  mainGroupId?: string;
  showFilterForTheMainGroup?: boolean;
  limit?: number;
  loadMoreBtnText?: string;
  showLoadMoreBtn?: boolean;
  titleTag?: string;
  [key: string]: unknown;
};

// Export preload function for use in processors
export const preloadPagesList = (
  mainGroupId: string | undefined,
  type: string | undefined,
  customFiltersByGroups: Record<string, string[]> | undefined,
  limit?: number
) => {
  // Preload categories if mainGroupId is provided
  if (mainGroupId) {
    preload(`/content/public/categories/${mainGroupId}/select-list`, fetcher);
  }

  // Preload first page of pages data if type is provided
  if (type) {
    const pageLimit = limit || DEFAULT_LIMIT;
    let filters: Record<string, string[]> = customFiltersByGroups || {};

    if (mainGroupId) {
      filters = {
        ...filters,
        [mainGroupId]: filters[mainGroupId] || []
      };
    }

    const params = new URLSearchParams();
    params.append('limit', pageLimit.toString());
    params.append('offset', '0');
    params.append('filters', JSON.stringify(filters));

    preload(`/content/public/page/list/by-type-alias/${type}?${params.toString()}`, fetcher);
  }
};

const PagesList: React.FC<Props> = ({
  type,
  template,
  customFiltersByGroups,
  mainGroupAlias,
  mainGroupId,
  showFilterForTheMainGroup,
  limit,
  loadMoreBtnText,
  showLoadMoreBtn,
  titleTag = 'h2',
  ...props
}) => {
  const t = usePublicTranslation();

  // Initialize activeCategory from URL on mount
  const [activeCategory, setActiveCategory] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(CATEGORY_QUERY_PARAM);
  });

  const pageLimit = limit || DEFAULT_LIMIT;

  // Update URL when activeCategory changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);

    if (activeCategory) {
      url.searchParams.set(CATEGORY_QUERY_PARAM, activeCategory);
    } else {
      url.searchParams.delete(CATEGORY_QUERY_PARAM);
    }

    window.history.replaceState({}, '', url.toString());
  }, [activeCategory]);

  const getKey = useCallback(
    (pageIndex: number, previousPageData: Pages | null) => {
      if (previousPageData && previousPageData.rows.length === 0) return null;

      if (!type) return null;

      let filters: Record<string, string[]> = customFiltersByGroups || {};

      if (mainGroupId) {
        filters = {
          ...filters,
          [mainGroupId]: activeCategory ? [activeCategory] : filters[mainGroupId] || []
        };
      }

      const params = new URLSearchParams();
      params.append('limit', pageLimit.toString());
      params.append('offset', (pageIndex * pageLimit).toString());
      params.append('filters', JSON.stringify(filters));

      return `/content/public/page/list/by-type-alias/${type}?${params.toString()}`;
    },
    [type, pageLimit, activeCategory, mainGroupId, customFiltersByGroups]
  );

  const { data: categories, isLoading: categoriesLoading } = useSWR<Category[]>(
    mainGroupId ? `/content/public/categories/${mainGroupId}/select-list` : null,
    fetcher
  );

  const filteredCategories = useMemo(() => {
    if (!categories) return [];

    if (customFiltersByGroups && mainGroupId && customFiltersByGroups[mainGroupId]) {
      return categories.filter(category => customFiltersByGroups[mainGroupId].includes(category.value));
    }

    return categories;
  }, [categories, customFiltersByGroups, mainGroupId]);

  const {
    data: pagesData,
    size,
    setSize,
    isLoading: pagesLoading,
    isValidating,
    mutate
  } = useSWRInfinite<Pages>(getKey, {
    revalidateFirstPage: false
  });

  // Reset and refetch when category changes
  useEffect(() => {
    setSize(1);
    mutate();
  }, [activeCategory, setSize, mutate]);

  const allPages = useMemo(() => {
    return pagesData?.flatMap(page => page.rows) || [];
  }, [pagesData]);

  const totalCount = pagesData?.[0]?.count || 0;

  const handleCategoryClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const category = event.currentTarget.getAttribute(CATEGORY_ATTRIBUTE);
    setActiveCategory(!!category?.length ? category : null);
  }, []);

  const handleLoadMoreClick = useCallback(() => {
    setSize(size + 1);
  }, [size, setSize]);

  const canLoadMore = useMemo(() => {
    if (!pagesData) return false;
    if (isValidating) return false;
    return totalCount > allPages.length;
  }, [pagesData, isValidating, totalCount, allPages.length]);

  return (
    <Fragment>
      {showFilterForTheMainGroup && !categoriesLoading && (
        <ul className="wp-block-erom-pages-list-categories">
          <li key="all" className={cn({ active: activeCategory === null })}>
            <button
              {...{
                [CATEGORY_ATTRIBUTE]: ''
              }}
              aria-current={activeCategory === null}
              onClick={handleCategoryClick}
            >
              {t('page.all')}
            </button>
          </li>
          {filteredCategories?.map(category => (
            <li key={category.value} className={cn({ active: activeCategory === category.value })}>
              <button
                {...{
                  [CATEGORY_ATTRIBUTE]: category.value
                }}
                aria-current={activeCategory === category.value}
                onClick={handleCategoryClick}
              >
                {category.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div {...props}>
        {!pagesLoading && totalCount === 0 && <p>{t('page.nothing_to_show')}</p>}
        {!pagesLoading &&
          !!allPages.length &&
          allPages.map(({ title, alias, createdOn, meta, rawBody, binders }, index) => {
            return (
              <Fragment key={index}>
                {template === TITLE_DATE_TEMPLATE && (
                  <TitleDateTemplate title={title} date={createdOn} alias={alias} titleTag={titleTag} />
                )}
                {template === IMAGE_LEFT_TEMPLATE && (
                  <ImageLeftTemplate
                    title={title}
                    date={createdOn}
                    image={meta?.featuredImage}
                    content={rawBody}
                    alias={alias}
                    titleTag={titleTag}
                  />
                )}
                {template === IMAGE_CATEGORY_TITLE_TEMPLATE && (
                  <ImageCategoryTitleTemplate
                    title={title}
                    image={meta?.featuredImage}
                    categories={
                      mainGroupAlias && binders?.[mainGroupAlias]
                        ? binders[mainGroupAlias]
                            .filter((c: string) => filteredCategories.some(fc => fc.value === c))
                            .map((c: string) => filteredCategories?.find(e => e.value === c)?.label || c)
                        : undefined
                    }
                    alias={alias}
                    titleTag={titleTag}
                  />
                )}
              </Fragment>
            );
          })}
      </div>
      {showLoadMoreBtn && canLoadMore && (
        <div className="wp-block-erom-pages-list-load-more">
          <button
            type="button"
            className="wp-block-erom-pages-list-load-more-button"
            onClick={handleLoadMoreClick}
            disabled={isValidating}
          >
            {isValidating ? t('page.loading') : loadMoreBtnText || t('page.load_more')}
          </button>
        </div>
      )}
    </Fragment>
  );
};

export default PagesList;
